import { filtersPending } from '../alpine/report-page';
import { pinnedBar } from './pinned-bar';

/**
 * The Alpine half of a report's filter bar (livewire/report/filter-bar.blade.php), nested in the
 * page's scope (ReportPage): which popover is open (a source's panel or a list filter's dropdown),
 * which of a panel's rows show their control, what a dropdown's trigger says, whether a filter's
 * control holds a pick not applied yet, the chips' ✕ and Clear all, and the cards a filter narrows lit up while its control is pointed at or focused
 * (rfb_reach()).
 *
 * Each popover is a disclosure, not a menu: it holds the filters' own controls, which no menu
 * pattern could carry (documentation/accessibility.md §9). Opening one moves focus into it (a
 * panel's first row, else its first control); Escape or its Done closes it and hands focus back to
 * its trigger, as a press elsewhere does when that press left focus nowhere; focus moving on past
 * it closes it where the reader is. The controls stay the hosts they are anywhere else, bound
 * deferred to Show::$pending, so nothing here changes a value: Apply applies the popovers' picks
 * as it applies the rest.
 *
 * Only the value filters have chips; the date, month and interval controls in the row show their
 * own values. A chip's ✕ and Clear all apply at once. Each tells the page's scope first, as Apply
 * does (`report-filters-applying`), so every card goes back to loading, and the press's own control
 * is gone once the answer lands, so focus goes to the chip that took its place, or the one before
 * it, or Apply filters when none is left.
 */

const hasOwn = (values, key) => values !== null && typeof values === 'object' && Object.prototype.hasOwnProperty.call(values, key);

/**
 * What a card on the open tab wears while a filter that narrows it is pointed at or focused: a 1px
 * ring in the brand's secondary colour and a soft glow of it around the card's own shadow (kept in
 * the list, so the card does not lose its shadow while lit). Both are box-shadows, so nothing about
 * the card's box, or where any other card sits, changes. The colour is the theme's brand token,
 * which resolves per host at runtime, mixed down for the glow. Important, as the card's own
 * `shadow` class would otherwise win on source order. Named here in full, so Tailwind builds them
 * from this file.
 */
export const REACH_CLASSES = [
    'ring-1',
    'ring-brand-secondary-500',
    '!shadow-[0_1px_3px_0_rgb(0_0_0/0.1),0_1px_2px_-1px_rgb(0_0_0/0.1),0_0_0_3px_color-mix(in_srgb,theme(colors.brand.secondary.500)_15%,transparent),0_8px_24px_-6px_color-mix(in_srgb,theme(colors.brand.secondary.500)_35%,transparent)]',
];

/**
 * Put on a card's box the first time the bar lights anything and left there, so the glow fades in
 * and out rather than snapping; none while motion is reduced.
 */
export const REACH_TRANSITION = ['transition-shadow', 'duration-150', 'motion-reduce:transition-none'];

/** What focus can land on inside a popover, in DOM order. */
const FOCUSABLE = 'input:not([disabled]):not([type="hidden"]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/**
 * Whether any of these filters' controls holds a pick the cards were not drawn with, compared as
 * the bar's own line compares the whole bar (filtersPending()). A filter the applied values do not
 * name is not the bar's, so it is never pending. Read by own key: a filter id may be any addressable
 * name, "constructor" included.
 *
 * @param {Record<string, unknown>|null|undefined} pending Show::$pending
 * @param {Record<string, unknown>|null|undefined} applied Show::$appliedBar
 * @param {string[]} ids
 */
export function pendingAmong(pending, applied, ids) {
    const subset = {};

    for (const id of ids) {
        if (hasOwn(applied, id)) subset[id] = applied[id];
    }

    return filtersPending(pending, subset);
}

/** A space-separated list of filter ids, as a source's button carries its filters' (`data-filters`). */
export function idsOf(list) {
    return String(list ?? '').split(' ').filter((id) => id !== '');
}

/**
 * Which chip takes focus once the chip at `index` is gone and `count` are left: the one that took
 * its place, else the last; -1 when none is left.
 */
export function chipAfterRemoval(index, count) {
    if (! Number.isInteger(count) || count <= 0) return -1;

    return Math.min(Math.max(Number.isInteger(index) ? index : 0, 0), count - 1);
}

/**
 * What a list filter's dropdown trigger says of the value its control holds, as
 * ReportFilterGroups::triggerText() says it for the first paint: `any` for no pick, the one pick's
 * label (the pick itself when it has none), or `many` with its count (":count selected").
 *
 * @param {unknown} value a Checkbox filter's list or a Radio filter's one value
 * @param {Record<string, string>} labels
 */
export function triggerText(value, labels, any, many) {
    const picks = value === null || value === undefined || value === ''
        ? []
        : (typeof value === 'object' ? Object.values(value) : [value]).map((pick) => String(pick));

    if (picks.length === 0) return any;
    if (picks.length === 1) return hasOwn(labels, picks[0]) ? String(labels[picks[0]]) : picks[0];

    return String(many).split(':count').join(String(picks.length));
}

/**
 * Runs once the answer to a Livewire call has settled on the page. Its promise resolves right after
 * the morph, while an element the morph patched can still carry the server's `x-cloak` (Alpine
 * takes it off again a moment later), which hides it and makes it refuse focus.
 */
const afterRender = (callback) => setTimeout(callback, 0);

/** The gap kept between a popover and the screen's edges, and between it and its trigger. */
const EDGE = 16;
const GAP = 4;

/** Below this width a popover spans the screen, as the row does: the app's sm breakpoint. */
const PHONE_WIDTH = 640;

/** The least room a popover is given below its trigger before the page is scrolled to make more. */
const MIN_ROOM = 240;

/**
 * Where a popover goes, in its frame's coordinates (the toolbar row, which it is positioned
 * against): always below its trigger, never flipped above it, where it would run off the top of the
 * screen. From sm up it starts at its trigger's left edge and is pushed back left just enough to end
 * inside the screen; on a phone it spans the screen, less the page's gutters. `maxHeight` is the room
 * left under the trigger when the popover is taller than that (it then scrolls inside), else null.
 *
 * @param {{left: number, top: number}} frame the frame's viewport position
 * @param {{left: number, bottom: number}} trigger the trigger's viewport rectangle
 * @param {number} width the popover's own width
 * @param {number} height its content's height
 * @param {{width: number, height: number}} screen the viewport
 * @returns {{left: number, top: number, width: number|null, maxHeight: number|null}}
 */
export function placement(frame, trigger, width, height, screen) {
    const isPhone = screen.width < PHONE_WIDTH;
    const boxWidth = isPhone ? screen.width - EDGE * 2 : Math.min(width, screen.width - EDGE * 2);
    const left = isPhone
        ? EDGE
        : Math.max(EDGE, Math.min(trigger.left, screen.width - EDGE - boxWidth));
    const room = Math.floor(screen.height - trigger.bottom - GAP - EDGE / 2);

    return {
        left: left - frame.left,
        top: trigger.bottom + GAP - frame.top,
        width: isPhone || boxWidth < width ? boxWidth : null,
        maxHeight: height > room ? Math.max(room, 160) : null,
    };
}

export default function ReportFilterBar() {
    // Kept out of the component's reactive state: an observer or a listener wrapped in Alpine's
    // proxy would be called on the proxy, which the browser refuses.
    let watcher = null;
    let onResize = null;

    return {
        // Pinned from lg up and folded to one line while the page scrolls under it (pinned-bar.js,
        // shared with the Site Analytics page's filter header). A popover open in it keeps it from
        // folding on its own, and folding it closes one.
        ...pinnedBar({
            section: '[data-report-filter-section]',
            sentinel: '[data-report-filter-sentinel]',
            toggle: '[data-report-filters-fold]',
            editBar: '[data-report-arrange-bar]',
            editBarHeight: '--report-edit-bar-height',
            height: '--report-filter-bar-height',
            pinnedAttribute: 'data-report-filters-pinned',
            editBarPinnedAttribute: 'data-report-edit-bar-pinned',
            gap: 24,
            isHeld() {
                return this.rfb_open !== '';
            },
            onFold() {
                this.rfb_close(false);
            },
        }),

        /** The open popover's key, a source's panel or a list filter's dropdown ('' when none is). */
        rfb_open: '',

        /** Filter id => whether its row in a source panel shows its control. */
        rfb_rows: {},

        rfb_isOpen(key) {
            return key !== '' && this.rfb_open === key;
        },

        /** Keeps an open popover placed as the window is resized or turned (rfb_placeOpen()). */
        init() {
            onResize = () => this.rfb_placeOpen();
            window.addEventListener('resize', onResize);

            this.pin_start();
        },

        destroy() {
            window.removeEventListener('resize', onResize);
            watcher?.disconnect();
            this.pin_stop();
        },

        /**
         * Whether the address holds the report's defaults: Show::$filters carries only what differs
         * from them, so it is empty. With no pick pending as well, Reset filters has nothing to do.
         */
        rfb_atDefaults() {
            const filters = this.$wire?.filters;

            return ! filters || typeof filters !== 'object' || Object.keys(filters).length === 0;
        },

        /**
         * Places a popover under its trigger (placement()), inside the screen. `scroll` is for the
         * moment it opens: when too little of the screen is left under the trigger, the page scrolls
         * the trigger up first, so the popover opens downward with room and focus lands where the
         * reader can see it. While it stays open it is only re-fitted, never scrolled to, as its
         * rows open and its hosts finish drawing (a ResizeObserver on its content).
         * Convention exception — inline style: its position, width and height are measured at
         * runtime against the trigger and the screen, which no class can hold.
         */
        rfb_place(popover, trigger, scroll = false) {
            if (! popover || ! trigger || ! popover.offsetParent) return;

            popover.style.maxHeight = '';
            popover.style.overflowY = '';
            popover.style.width = '';

            const screen = { width: document.documentElement.clientWidth, height: window.innerHeight };
            const content = () => popover.scrollHeight;

            // A pinned section stays put as the page scrolls, so scrolling its trigger up gains nothing.
            if (scroll && ! this.pin_isPinned() && screen.height - trigger.getBoundingClientRect().bottom < Math.min(content(), MIN_ROOM)) {
                trigger.scrollIntoView({ block: 'start' });
            }

            const spot = placement(popover.offsetParent.getBoundingClientRect(), trigger.getBoundingClientRect(), popover.offsetWidth, content(), screen);

            popover.style.left = `${spot.left}px`;
            popover.style.top = `${spot.top}px`;
            popover.style.width = spot.width === null ? '' : `${spot.width}px`;
            popover.style.maxHeight = spot.maxHeight === null ? '' : `${spot.maxHeight}px`;
            popover.style.overflowY = spot.maxHeight === null ? '' : 'auto';

            if (scroll) this.rfb_watch(popover, trigger);
        },

        /** Re-fits the open popover as its content changes size, until it closes. */
        rfb_watch(popover, trigger) {
            watcher?.disconnect();

            if (typeof ResizeObserver !== 'function') return;

            watcher = new ResizeObserver(() => {
                if (popover.offsetParent) this.rfb_place(popover, trigger);
            });
            Array.from(popover.children).forEach((child) => watcher.observe(child));
        },

        /** The open popover placed again (a resize). */
        rfb_placeOpen() {
            if (this.rfb_open !== '') this.rfb_place(this.rfb_part('panel', this.rfb_open), this.rfb_part('trigger', this.rfb_open));
        },

        /**
         * A popover's trigger pressed: opens it with focus inside (rfb_focusInto()), or closes it
         * with focus left on the trigger that closed it. One popover is open at a time.
         */
        rfb_toggle(key) {
            if (this.rfb_isOpen(key)) {
                this.rfb_open = '';

                return;
            }

            this.rfb_open = key;
            this.$nextTick(() => {
                const popover = this.rfb_part('panel', key);

                this.rfb_place(popover, this.rfb_part('trigger', key), true);
                this.rfb_focusInto(popover);
            });
        },

        /**
         * Focus into a popover as it opens: a source panel's first row, else the first control shown
         * in it, else the popover itself (its controls still loading), which Tab leaves for them.
         */
        rfb_focusInto(popover) {
            if (! popover) return;

            const row = popover.querySelector('[data-report-filter-row]');
            const control = Array.from(popover.querySelectorAll(FOCUSABLE)).find((element) => element.getClientRects?.().length > 0);

            (row ?? control ?? popover).focus();
        },

        /** Closes the open popover, handing focus back to its trigger when asked. */
        rfb_close(returnFocus = false) {
            const key = this.rfb_open;

            if (key === '') return;

            this.rfb_open = '';
            watcher?.disconnect();

            if (returnFocus) this.rfb_part('trigger', key)?.focus();
        },

        /**
         * Escape inside a popover's group: closes it and returns focus to its trigger. A host whose
         * own list is open takes the Escape first and stops it, so the list closes and the popover
         * stays.
         */
        rfb_escape(event, key) {
            if (! this.rfb_isOpen(key)) return;

            event.stopPropagation();
            this.rfb_close(true);
        },

        /**
         * A press outside a popover's group closes it. Focus goes back to its trigger only when the
         * press left it nowhere (on the page's background) or inside the closing popover; a press on
         * another control keeps the focus that press gave it.
         */
        rfb_pressedAway(key) {
            if (! this.rfb_isOpen(key)) return;

            const popover = this.rfb_part('panel', key);
            const active = document.activeElement;

            this.rfb_close(! active || active === document.body || Boolean(popover?.contains(active)));
        },

        /**
         * Focus left a popover's group for somewhere outside it (Tab on past it): it closes, as a
         * press elsewhere closes it. Focus with nowhere named (a host redrawing its control) leaves
         * it open.
         */
        rfb_focusLeft(event, key) {
            const next = event.relatedTarget;

            if (this.rfb_isOpen(key) && next instanceof Node && ! event.currentTarget.contains(next)) this.rfb_close(false);
        },

        rfb_rowOpen(id) {
            return hasOwn(this.rfb_rows, id) && this.rfb_rows[id] === true;
        },

        rfb_toggleRow(id) {
            this.rfb_rows = { ...this.rfb_rows, [id]: ! this.rfb_rowOpen(id) };
        },


        /**
         * A popover row's value line for its filter's pending value, so it follows the control under
         * it as picks are made: a list's picks by label, one pick by label, a Boolean's tick, "Any"
         * for none. A date, month or interval filter's line stays the applied value in words
         * (`data-fixed`), its "Not applied" mark saying when the control holds something else.
         */
        rfb_rowText(line) {
            const data = line?.dataset ?? {};

            if (data.kind === 'fixed') return data.fixed ?? '';

            let labels = {};

            try {
                labels = JSON.parse(data.labels ?? '{}') ?? {};
            } catch {
                labels = {};
            }

            const pending = this.$wire?.pending;
            const value = hasOwn(pending, data.filter) ? pending[data.filter] : null;
            const picks = value === null || value === undefined || value === ''
                ? []
                : (typeof value === 'object' ? Object.values(value) : [value]).map((pick) => String(pick));

            if (picks.length === 0) return data.any ?? 'Any';
            if (data.kind === 'bool') return data.yes ?? 'Yes';

            return picks.map((pick) => (hasOwn(labels, pick) ? String(labels[pick]) : pick)).join(', ');
        },

        /**
         * A source panel's "Clear <source>": that source's value filters emptied and applied at once
         * (Show::clearFilters($source)). The link is gone once nothing is left to clear, so focus
         * goes into the panel, which stays open. The root is read first, as rfb_removeChip() does.
         */
        rfb_clearSource(button, key) {
            const root = this.$root;

            this.$dispatch('report-filters-applying');
            this.$wire.clearFilters(button.dataset.source)
                .then(() => afterRender(() => this.rfb_focusInto(root?.querySelector(`[data-report-filter-panel="${key}"]`))));
        },

        /** Whether these filters' controls hold a pick not applied yet (idsOf() list). */
        rfb_pending(list) {
            return pendingAmong(this.$wire?.pending, this.$wire?.appliedBar, idsOf(list));
        },

        /**
         * A dropdown trigger's text for its filter's pending value (triggerText()), so it follows the
         * control inside it as picks are made. The trigger carries the filter id, the labels and the
         * two words as data; an unreadable labels map reads every pick as itself.
         */
        rfb_summary(trigger) {
            const data = trigger?.dataset ?? {};
            let labels = {};

            try {
                labels = JSON.parse(data.labels ?? '{}') ?? {};
            } catch {
                labels = {};
            }

            const pending = this.$wire?.pending;

            return triggerText(hasOwn(pending, data.filter) ? pending[data.filter] : null, labels, data.any ?? 'Any', data.many ?? ':count selected');
        },

        /**
         * A chip's ✕: that pick taken away, or its filter cleared, and applied at once. The bar's root
         * is read before the call: the magics a handler sees are bound to the element it runs on,
         * and this ✕ is gone from the page by the time the answer lands, so `$root` read then would
         * find nothing.
         */
        rfb_removeChip(button) {
            const root = this.$root;
            const index = this.rfb_chips(root).indexOf(button);

            this.$dispatch('report-filters-applying');
            this.$wire.removeFilterValue(button.dataset.filter, button.dataset.value ?? null)
                .then(() => afterRender(() => this.rfb_focusChip(root, index)));
        },

        /**
         * Clear all: every value filter on the tab (the ones with chips) emptied and applied at once;
         * the date, month and interval filters keep their values. The chips, Clear all among them, are
         * gone once it lands, so focus goes to Apply.
         */
        rfb_clearAll() {
            const root = this.$root;

            this.$dispatch('report-filters-applying');
            this.$wire.clearFilters().then(() => afterRender(() => this.rfb_focusChip(root, -1)));
        },

        rfb_chips(root) {
            return Array.from(root?.querySelectorAll('[data-report-filter-chip]') ?? []);
        },

        rfb_focusChip(root, index) {
            const chips = this.rfb_chips(root);
            const at = index < 0 ? -1 : chipAfterRemoval(index, chips.length);

            (at >= 0 ? chips[at] : root?.querySelector('[data-report-filters-apply]'))?.focus();
        },

        /**
         * A filter's control pointed at or focused: the cards on the open tab it narrows (the ids its
         * element carries, `data-narrows`, from Show::filterBar()) light up, and every other card goes
         * back. Nothing lights while a press is held down, which is a drag passing over the bar (edit
         * mode's cards, the palette's entries), or while SortableJS holds an item.
         */
        rfb_reach(element, event) {
            if (event?.buttons || document.querySelector('.sortable-chosen, .sortable-drag')) return;

            this.rfb_light(idsOf(element?.dataset?.narrows));
        },

        rfb_unreach() {
            this.rfb_light([]);
        },

        /** Focus left a filter's control: its cards go back, unless focus moved within it. */
        rfb_reachLeft(element, event) {
            const next = event?.relatedTarget;

            if (next instanceof Node && element.contains(next)) return;

            this.rfb_light([]);
        },

        /**
         * These cards lit, every other card on the page not. A card is the element carrying its id
         * (`data-site-analytics-card`, card.blade.php), and what lights is its box (`data-card`);
         * `data-filter-reach` marks a lit card for anything reading the page. A render drawing the
         * cards again drops the classes, as the pointer's next move would.
         */
        rfb_light(ids) {
            const lit = new Set(ids);

            for (const card of document.querySelectorAll('[data-site-analytics-card]')) {
                const isLit = lit.has(card.dataset.siteAnalyticsCard);
                const box = card.querySelector(':scope > [data-card]');

                card.toggleAttribute('data-filter-reach', isLit);
                box?.classList.add(...REACH_TRANSITION);
                REACH_CLASSES.forEach((name) => box?.classList.toggle(name, isLit));
            }
        },

        /** A popover's trigger or panel, by its key (a hash, so safe in a selector). */
        rfb_part(part, key) {
            return this.$root.querySelector(`[data-report-filter-${part}="${key}"]`);
        },
    };
}
